import * as React from 'react';
import axios, { CancelTokenSource } from 'axios';
import themePrimary from '../../styles/themePrimary';
import { Typography, makeStyles, useMediaQuery, Dialog, AppBar, IconButton, Toolbar, Slide } from '@material-ui/core';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import CloseIcon from '@material-ui/icons/Close';
import { TransitionProps } from '@material-ui/core/transitions';
import { String_ViewTermsPopup_TermsToggle, String_ViewTermsPopup_PrivacyToggle, String_ViewTermsPopup_Title } from '../../utilities/StringHelper';
import { ElementDimensions } from '../../styles/ElementDimensions';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { GetTermsResponse } from '../../models/payment/GetTermsResponse';
import { Skin } from '../../models/common/Skin';
import { CheckStateContext } from '../../providers/CheckStateContextProvider';

const useStyles = makeStyles({
    appBar: {
        height: ElementDimensions.dialogBarHeight + 'px',
        position: 'relative',
    },
    closeButton: {
        height: '30px',
        width: '30px',
        color: themePrimary.palette.primary.contrastText,
    },
    details: {
        position: 'absolute',
        top: (ElementDimensions.dialogBarHeight + ElementDimensions.toggleButtonHeight - 15) + 'px',
        bottom: 0,
        left: 0,
        right: 0,
        padding: ElementDimensions.padding10 + 'px',
        width: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    dialog: {
        backgroundColor: themePrimary.palette.secondary.light,
    },
    dialogLandscape: {
        backgroundColor: themePrimary.palette.secondary.light,
        left: 'calc(100vw *.3) !important',
        width: 'calc(100vw *.7)',
    },
    footerButtons: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: ElementDimensions.footerBarHeight + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: '100%',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    footerButtonsLandscape: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        left: 'calc(100vw *.3)',
        height: ElementDimensions.footerBarHeightLandscape + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: 'calc(100vw * .7)',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    headerContainer: {
        backgroundColor: themePrimary.palette.secondary.main,
        paddingLeft: ElementDimensions.padding10 + 'px',
        paddingRight: ElementDimensions.padding10 + 'px',
        width: '100%',
    },
    headerTextLeft: {
        fontSize: '1rem',
        lineHeight: '2.2rem',  
    },
    loadingContainer: {
        margin: ElementDimensions.margin20 + 'px',
        textAlign: 'center',
    },
    termsHeader: {
        fontWeight: 'bold',
        color: themePrimary.palette.secondary.contrastText,
    },
    termsText: {
        color: themePrimary.palette.secondary.contrastText,
    },
    termsTitle: {
        color: '#350080',
    },
    title: {
        marginLeft: ElementDimensions.margin10 + 'px',
        flex: 1,
        color: themePrimary.palette.primary.contrastText,
    },
    toggleButton: {
        width: '50%',
        borderRadius: '0px',
        padding: ElementDimensions.padding5 + 'px',
        marginLeft: '0px !important',
    },
    toggleButtonIcon: {
        marginRight: ElementDimensions.margin20 + 'px',
        width: '30px',
        height: '30px',
    },
    toggleButtonIconSelected: {
        marginRight: ElementDimensions.margin20 + 'px',
        width: '30px',
        height: '30px',
    },
    toggleButtonSelected: {
        width: '50%',
        borderRadius: '0px',
        padding: ElementDimensions.padding5 + 'px',
        backgroundColor: props => (props as Skin).primary_main_color + ' !important',
        color: themePrimary.palette.primary.contrastText + ' !important',
    },
    toggleGroup: {
        width: '100%',
        marginBottom: ElementDimensions.margin10 + 'px',
        borderRadius: '0px',
    },
});

export type TermsPopupCloseClickHandler = (event: React.MouseEvent<HTMLElement>) => any;

export interface TermsPopupProps {
    open: boolean;
    onCloseHandler: TermsPopupCloseClickHandler;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement },
    ref: React.Ref<unknown>,
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const TermsPopup = (props: TermsPopupProps) => {
    const { open, onCloseHandler } = props;
    const [toggle, setToggle] = React.useState<string>('terms');
    const [termsText, setTermsText] = React.useState<{ __html: string} | undefined>(undefined);
    const [privacyText, setPrivacyText] = React.useState<{ __html: string} | undefined>(undefined);
    const isPortrait = true;
    const userState = React.useContext(UserStateContext);
    const checkState = React.useContext(CheckStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    React.useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();

        if (termsText === undefined && privacyText === undefined) {
            loadData(source);
        }
        
        return () => {
            source.cancel();
        };
    }, []);

    const loadData = async (cancelTokenSource: CancelTokenSource) => {

        let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + checkState.state.checkSummary!!.id + "/payment/terms"
    
        const headers = {
            'X-API-Key': checkOutState.state.restaurantApiKey,
            'location_id': checkOutState.state.restaurantId
        }
        

        axios.get(url, { headers, cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as GetTermsResponse;
            if (r.success === true) {
                let markupTerms = {
                    __html: r.terms,
                };

                setTermsText(markupTerms);

                let markupPrivacy = {
                    __html: r.privacy,
                };

                setPrivacyText(markupPrivacy);
            }
        });
    };

    const handleClose = (event: React.MouseEvent<HTMLElement>) => {
        onCloseHandler(event);
    };

    const handleToggle = (event: React.MouseEvent<HTMLElement>, newToggle: string) => {
        setToggle(newToggle);
    };

    return (
        <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition} className={isPortrait === true ? classes.dialog : classes.dialogLandscape}>
            <AppBar className={classes.appBar}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                        <CloseIcon className={classes.closeButton}/>
                    </IconButton>
                    <Typography variant="h6" className={classes.title}>
                        {String_ViewTermsPopup_Title.find(s => s.key === userState.state.locale)!!.value}
                    </Typography>
                </Toolbar>
            </AppBar>
            <ToggleButtonGroup exclusive value={toggle} onChange={handleToggle} className={classes.toggleGroup}>
                <ToggleButton value="terms" className={toggle === 'terms' ? classes.toggleButtonSelected : classes.toggleButton}>
                    {String_ViewTermsPopup_TermsToggle.find(s => s.key === userState.state.locale)!!.value}
                </ToggleButton>
                <ToggleButton value="privacy" className={toggle === 'privacy' ? classes.toggleButtonSelected : classes.toggleButton}>
                    {String_ViewTermsPopup_PrivacyToggle.find(s => s.key === userState.state.locale)!!.value}
                </ToggleButton>
            </ToggleButtonGroup>

            {toggle === 'terms' &&
                <div id="termsContainer" dangerouslySetInnerHTML={termsText} className={classes.details}>
                </div>
            }
            
            {toggle === 'privacy' &&
                <div id="privacyContainer" dangerouslySetInnerHTML={privacyText} className={classes.details}>
                </div>
            }
        </Dialog>
    );
};

export default TermsPopup;